import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { AuthenticationService } from '../services/authentication.service';
import { EntityService } from '../services/entity.service';
import { PayCategoryService } from '../services/pay-category.service';
import { PositionService } from '../services/position.service';
import { PpsUtilityService } from '../services/pps-utility.service';
import { BusinessOperation } from '../shared/models/business-operation';
import { IEntity } from '../shared/models/entity';
import { EntityCategory } from '../shared/models/entity-category';
import { Position } from '../shared/models/position';
import { ToastService } from '../shared/toast/toast.service';
import { PositionCheckboxComponent } from './position-checkbox.component';
import { EntityActionComponent } from './entity-action.component';
import { EntityFormComponent } from './entity-form.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { SubSink } from 'subsink';
import { GridCheckboxComponent } from './grid-checkbox.component';

@Component({
  selector: 'pps-system-data-management',
  templateUrl: './system-data-management.component.html',
  styleUrls: ['./system-data-management.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class SystemDataManagementComponent implements OnInit, OnDestroy {
  private subs = new SubSink();
  
  smallScreen: boolean = true;
  items: MenuItem[] = [];
  spinnerName: string = "dataSpinner";
  private itemIcons = {
    "Client Engineer": "pi-fw fas fa-user-cog",
    "Work Group":"pi-fw fas fa-user-friends",
    "Position":"pi-fw fas fa-address-card",
    "Pay Category":"pi-fw fas fa-money-check-alt"
  }
  
  entities: any[] = [];
  gridHeaders: any[];
  context:any = {};
  private gridApi;
  entityLoading: boolean = false;
  private tipInfo: string = "";
  selectedCategory: EntityCategory = EntityCategory.Area
  @Input() selectedCategoryName: string = "";

  gridOptions = {
    getRowId: param => param.data.id,
    defaultColDef:{
      floatingFilter: true,
      enableCellChangeFlash: true
    }
  };
  
  constructor(private _route: ActivatedRoute,
              private _entityService: EntityService,
              private _dialog: DialogService,
              private _confirm: ConfirmationService,
              private _positionService: PositionService,
              private _payCategoryService: PayCategoryService,
              private _util: PpsUtilityService,
              private _toast: ToastService,
              private _spinner: NgxSpinnerService,
              private breakpointObserver: BreakpointObserver) { 
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });    
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.context = { componentParent: this };

    this.subs.sink = this._route.queryParams
      .subscribe(params => {
        this.selectedCategoryName = params.entity || "Client Engineer";

        this.tipInfo = `
          Double click on the ${this.selectedCategoryName} field or press F2 when focus on the ${this.selectedCategoryName} field to 
          modify existing value. Press Enter to complete the ${this.selectedCategoryName} update.
        `;
        this.items = [
          {label:"Settings", icon: "pi-fw fas fa-tools"}, 
          {label:"Manage " + this.selectedCategoryName, icon: this.itemIcons[this.selectedCategoryName]}
        ];

        this.gridHeaders = this.getColumnDefs(this.selectedCategoryName);
        this.selectCategory(this.selectedCategoryName);
        if (window.sessionStorage && window.sessionStorage.getItem("show-setting-tip") !== "1" ){
          this._toast.message({"detail": this.tipInfo, life: 100000, key: "for-help", severity: "success" });
          window.sessionStorage.setItem("show-setting-tip", "1");
        }
      });
  }

  ngOnChanges() {
    this.gridHeaders = this.getColumnDefs(this.selectedCategoryName);
    this.selectCategory(this.selectedCategoryName);
  }  

  onGridReady(params) {
    this.gridApi = params.api;
  }

  selectCategory(categoryName: string){
    this.selectedCategoryName = categoryName;
    this.selectedCategory = this._util.getEntityCategory(categoryName);

    if (this.gridApi){
      this.gridApi.setColumnDefs(this.getColumnDefs(categoryName));
    }

    if (this.isPosition()){
      this.loadPositions();
    }else if (this.isPayCategory()){
      this.loadPayCategories();
    }else{
      this.loadSelectedEntity();
    }
  }

  loadPositions(){
    this.entityLoading = true;
    this._spinner.show(this.spinnerName);
    this.subs.sink = this._positionService.getAll()
      .subscribe(res => this.afterEntityLoaded(res, p => {
        return {id: p.id, name: p.title, abbr: p.abbr, salaryEmployee: p.salaryEmployee, leadHead: p.leadHead, logRnR: p.logRnR};
      }))
      .add(()=>{
        this.entityLoading = false;
        this._spinner.hide(this.spinnerName);
      });
  }

  updatePosition(position: Position){
    this.subs.sink = this._positionService.update(position)
      .subscribe(res => {
        if (res.succeed){
          this._toast.succeeded("Position updated", 2000, "for-entity");
        }else{
          this._toast.failed("Position updated failed: " + res.errors.join(";"), 50000, "for-entity");
        }
      })
      .add(()=>{
        this.entityLoading = false;
      });
  }

  loadPayCategories(){
    this.entityLoading = true;
    this._spinner.show(this.spinnerName);
    this.subs.sink = this._payCategoryService.getAll()
      .subscribe(res => this.afterEntityLoaded(res, p => {
        return {id: p.id, name: p.name, entityCategory: EntityCategory.Unknown, active: true, sortNo: 0};
      }))
      .add(()=>{
        this.entityLoading = false;
        this._spinner.hide(this.spinnerName);
      });
  }  

  loadSelectedEntity(){
    if (!this.selectedCategory){
      return;
    }
    
    this.entityLoading = true;
    this._spinner.show(this.spinnerName);
    this.subs.sink = this._entityService.getEntitiesByCategory(this.selectedCategory)
      .subscribe(res => this.afterEntityLoaded(res, null))
      .add(()=>{
        this.entityLoading = false;
        this._spinner.hide(this.spinnerName);
      });
  }

  afterEntityLoaded(apiResponse, mapper){
    if (apiResponse.succeed){
      if (mapper){
        this.entities = apiResponse.result.map(p => mapper(p));  
      }else{
        this.entities = apiResponse.result
      }

      if (this.isWorkGroup()){
        try{
          this.entities = this.entities.sort((a, b)=>{
            return a.sortNo - b.sortNo;
          });
        }
        catch(ex){}
      }

      this._toast.succeeded(`${this.entities.length} ${this.selectedCategoryName}s loaded`, 2000, "for-entity");
    }else{
      this._toast.failed(`Failed to load positions: ${apiResponse.errors.join(";")}`, 50000, "for-entity");
    }
  }

  isActive(categoryName: string): boolean{
    return this.isEqual(categoryName);
  }

  isWorkGroup(): boolean{
    return this.isEqual("Work Group");
  }

  isPosition(): boolean{
    return this.isEqual("Position");
  }

  isPayCategory(): boolean{
    return this.isEqual("Pay Category");
  }
  
  isEqual(category): boolean{
    return (category.toLowerCase().replace(" ", "") === this.selectedCategoryName.toLowerCase().replace(" ", ""));
  }

  openEntityForm(entity: IEntity){
    if (entity === null){
      entity = {id: 0, name: "", entityCategory: this.selectedCategory, active: true, sortNo: 0};
    }

    let dialogOption: any = {
      backdrop: 'static',
      size: "550"
    };

    let dialog = this._dialog.open(EntityFormComponent, {
      header: `Add ${this.selectedCategoryName}`,
      styleClass: "size-sm",
      data: {
        entity: entity,
        categoryName: this.selectedCategoryName
      },
      draggable: true
    });

    this.subs.sink = dialog.onClose.subscribe(entity => {
      if (entity)
      {
        this.save(entity);
      }},
      reason => {
        console.log(reason);
      }
    );
  }

  private save(entity: IEntity){
    if (this.isPosition()){
      this.savePosition(entity);
    }else if (this.isPayCategory()){
      this.savePayCategory(entity);
    }else{
      this.saveEntity(entity);
    }
  }

  private saveEntity(entity: IEntity){
    this.subs.sink = this._entityService
      .addEntity(entity)
      .subscribe(res => {
        this.afterEntitySaved(res, entity);
      });
  }

  private savePosition(entity: IEntity){
    let pos = {id: entity.id, title: entity.name, abbr: null, active: true}
    this.subs.sink = this._positionService
      .add(pos)
      .subscribe(res => {
        let result = {id: res.result.id, name: res.result.title, entityCategory: EntityCategory.Unknown, active: true, sortNo: 0}
        this.afterEntitySaved(res, entity);
      });
  }

  private savePayCategory(entity: IEntity){
    this.subs.sink = this._payCategoryService
      .add(entity)
      .subscribe(res => {
        this.afterEntitySaved(res, entity);
      });
  }

  private afterEntitySaved(apiResponse, entity:IEntity){
    if (apiResponse.succeed){
      entity.id = apiResponse.result.id;
      this.entities.unshift(entity);
      this.gridApi.setRowData(this.entities);
      let rowNode = this.gridApi.getRowNode(apiResponse.result.id);
      if (rowNode){
        this.gridApi.flashCells({rowNodes: [rowNode]});
        rowNode.setSelected(true);
        
        window.setTimeout( () =>{
          this.gridApi.startEditingCell({rowIndex: rowNode.rowIndex, colKey: "name"});
        }, 1000);
      }
      this._toast.succeeded(`${this.selectedCategoryName}: [${entity.name}] create successfully`, 2000, "for-entity");
    }else{
      this._toast.failed(`${this.selectedCategoryName}: [${entity.name}] creatation failed: ${apiResponse.errors.join(";")}`);
    }
  }

  deleteEntity(entity: IEntity){
    if (entity === null){
      return;
    }

    this._confirm.confirm({
        header: `Delete ${this.selectedCategoryName}`,
        message: `Are you sure that you want to delete ${this.selectedCategoryName}: [${entity.name}]?`,
        icon: "fas fa-exclamation-circle icon-danger",
        defaultFocus: "accept",
        accept: () => {
          if (this.isPosition()){
            this.subs.sink = this._positionService.delete(entity.id)
              .subscribe(res => this.afterEntityDeleted(res, entity));
          }else{
            this.subs.sink = this._entityService.deleteEntity(entity.id)
              .subscribe(res => this.afterEntityDeleted(res, entity));
          }
        }});
  }

  private afterEntityDeleted(apiResponse, entity: IEntity){
    if (apiResponse.succeed){
      var foundIndex = this.entities.findIndex(x => x.id == entity.id);
      if (foundIndex !== -1 ){
        this.entities.splice(foundIndex, 1);
        this.gridApi.setRowData(this.entities);
        this._toast.succeeded(`${this.selectedCategoryName}: [${entity.name}] deleted!`, 2000, "for-entity");
      }else{
        this._toast.failed(`Delete ${this.selectedCategoryName}: [${entity.name}] failed!`, 5000, "for-entity");
      }
    }else{
      this._toast.failed(`Failed to delete ${this.selectedCategoryName}: [${entity.name}]: ${apiResponse.errors.join("|")}`, 5000, "for-entity");
    }
  }

  private updateData(params){
    var entity = params as IEntity;
    this.subs.sink = this._entityService.updateEntity(entity).subscribe(res => this.afterEntityUpdated(res));
  }

  private entityNameChanged(params){
    let _this = params.context.componentParent;

    if (_this.isPosition()){
      let pos = {id: params.data.id, title: params.data.name, abbr: params.data.abbr || "", active: true};
      _this.subs.sink = _this._positionService.update(pos).subscribe(res => _this.afterEntityUpdated(res));
    }else if (_this.isPayCategory()){
      _this.subs.sink = _this._payCategoryService.update(params.data).subscribe(res => _this.afterEntityUpdated(res));
    }else{
      let entity = params.data as IEntity;
      entity.sortNo = +entity.sortNo;
      _this.subs.sink = _this._entityService.updateEntity(entity).subscribe(res => _this.afterEntityUpdated(res));
    }
  }  

  private afterEntityUpdated(apiResponse){
    if (apiResponse.succeed){
      this._toast.succeeded(`${this.selectedCategoryName} updated`, 2000, "for-entity");
    }else{
      this._toast.failed(`${this.selectedCategoryName} update failed: ${apiResponse.errors.join("|")}`, 2000, "for-entity");
    }
  }

  private getColumnDefs(category){
    let cols: any[] = [
      {
        headerName: "",
        field: "action",
        cellRenderer: EntityActionComponent,
        width: 40,
        hide: false,
        filter: false,
        sortable: false,
        cellStyle: {textAlign:"center"}
      },
      {
        headerName: this.selectedCategoryName,
        field:'name',
        filter: true,
        sortable: true,
        resizable: true,
        flex: 1,
        editable: true,
        onCellValueChanged: this.entityNameChanged,
        headerClass: "ag-ext-header-align-left"
      }
    ];
    if (category === "Work Group"){
      cols.splice(1, 0, {
          headerName: 'Order No',
          field:'sortNo',
          width: 60,
          editable: true,
          sortable: true,
          onCellValueChanged: this.entityNameChanged,
          cellStyle: {textAlign:"center"}
        });
      cols.push({
          colId: "responsibleEngineerGroup",
          headerName: "Responsible Engineer Group",
          field: "responsibleEngineerGroup",
          sortable: true,
          width: 200,
          cellRenderer: GridCheckboxComponent,
          cellRendererParams: {
            checkboxValueColId: "responsibleEngineerGroup"
          },          
          cellStyle: {textAlign:"center"}
        });
    }

    if (category === "Position"){
      cols.push({
        headerName: 'Abbr',
        field:'abbr',
        filter: true,
        sortable: true,
        width: 120,
        editable: true,
        resizable: true,
        onCellValueChanged: this.entityNameChanged
      });

      cols.push({
        colId: "salary-employee",
        headerName: "Salaried Position",
        field: "salaryEmployee",
        sortable: true,
        width: 120,
        cellRenderer: PositionCheckboxComponent,
        cellStyle: {textAlign:"center"}
      });

      cols.push({
        colId: "lead-head",
        headerName: "Lead Head",
        field: "leadHead",
        sortable: true,
        width: 120,
        cellRenderer: PositionCheckboxComponent,
        cellStyle: {textAlign:"center"}
      });

      cols.push({
        colId: "logRnR",
        headerName: "RnR to be entered into timesheets",
        field: "logRnR",
        sortable: true,
        width: 240,
        cellRenderer: PositionCheckboxComponent,
        cellStyle: {textAlign:"center"}
      });
    }

    return cols;
  }
}
